import React, { useState, useEffect } from 'react';
import { getPersons, addPerson } from '../api';
import { usePagination } from '../utils';

function Persons() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '' });
  const personsPagination = usePagination(persons);

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadPersons();
  }, []);

  const loadPersons = async () => {
    try {
      setLoading(true);
      const response = await getPersons();
      setPersons(response.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری اشخاص');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('نام شخص الزامی است');
      return;
    }

    try {
      await addPerson(formData);
      setSuccess('شخص با موفقیت اضافه شد');
      setFormData({ name: '' });
      loadPersons();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در افزودن شخص');
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-1">افزودن شخص جدید</h2>
              <p className="text-muted small mb-0">نام را وارد و ثبت کنید</p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">نام شخص</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    placeholder="نام و نام خانوادگی"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary w-100">
                    ثبت شخص
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
              <div>
                <h2 className="h5 mb-1">لیست اشخاص</h2>
                <p className="text-muted small mb-0">مجموع افراد ثبت‌شده</p>
              </div>
              <span className="badge bg-primary text-white px-3 py-2">{persons.length} رکورد</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">ردیف</th>
                      <th scope="col">نام</th>
                      <th scope="col">تاریخ ثبت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personsPagination.totalItems === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">
                          هنوز شخصی ثبت نشده است
                        </td>
                      </tr>
                    )}
                    {personsPagination.paginatedItems.map((person, index) => (
                      <tr key={person.id}>
                        <td>
                          <span className="badge bg-secondary">{personsPagination.rangeStart + index}</span>
                        </td>
                        <td className="fw-semibold">{person.name}</td>
                        <td>{new Date(person.created_at).toLocaleDateString('fa-IR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPaginationControls(personsPagination)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Persons;

